/* ============================================================
   SEKSYEN DASHBOARD YANG BELUM DIREVAMP
   ============================================================
   Tiga seksyen di bawah dipapar SAMA pada kedua-dua rupa —
   rupa lama (GettingStartedView) dan rupa v3.9 (DashboardV39View).
   Revamp v3.9 dibuat satu seksyen demi satu; ini yang belum
   sampai giliran, jadi bentuk, susunan dan fungsinya dikekalkan
   bulat-bulat.

   Sebab ia duduk dalam fail sendiri dan bukan disalin ke dalam
   dua fail rupa: selagi ia "sama", ia mesti BENAR-BENAR sama.
   Dua salinan akan terpesong senyap.

   Bila tiba giliran seksyen ini direvamp, DashboardV39View
   berhenti mengimport bahagian berkenaan dan menggantikannya
   dengan versi v3.9; rupa lama kekal memakai fail ini.

   ⚠️ Warna di sini ikut tema terang/gelap app lama (kelas
      `dark:` Tailwind). Dalam v3.9 ia dibalut satu bekas
      berkelas `dark` supaya ia memakai palet gelapnya sendiri
      — tanpa itu kotak putih menyilaukan dalam panel gelap.
   ============================================================ */

import React from 'react';
import { useNavigate } from 'react-router-dom';

/* ⭐ PROP `t` — kunci kepada dua bahasa TANPA mengubah rupa lama.
   Halaman v3.9 menghantar fungsi terjemahannya masuk; rupa lama
   (GettingStartedView) tidak menghantar apa-apa dan kekal dengan
   ayat Melayu di bawah, huruf demi huruf.

   Kenapa bukan panggil useT() terus di sini: fail ini dikongsi oleh
   KEDUA-DUA rupa, dan bahasa lalai app ialah English. Panggilan terus
   bermakna pelanggan yang masih pada rupa LAMA — iaitu semua orang
   sehingga v3.9 dilancarkan — tiba-tiba nampak tiga blok English
   terselit dalam dashboard Melayu. Itu pepijat yang sama yang kita
   cuba elak, cuma berpindah tempat.

   Bila v3.9 sudah menjadi satu-satunya rupa, prop ini boleh dibuang
   dan useT() dipanggil terus. */
const ambilTeks = (t) => (kunci, lalai, vars) => (t ? t(kunci, vars) : lalai);

/* ── Notis: akaun Google belum ada kata laluan ─────────────── */
export function PasswordSetupNotice({ show, t }) {
    const navigate = useNavigate();
    const T = ambilTeks(t);
    if (!show) return null;

    return (
        <section className="w-full px-4 sm:px-6 pt-4">
            <button
                onClick={() => navigate('/profile/security')}
                className="w-full flex items-center gap-3 text-left px-4 py-3 rounded-xl border border-amber-200 dark:border-amber-800 bg-amber-50 dark:bg-amber-900/20 hover:bg-amber-100 dark:hover:bg-amber-900/30 transition-colors"
            >
                <span className="material-symbols-outlined text-amber-500 text-[20px] flex-shrink-0">key</span>
                <span className="flex-1 text-xs sm:text-sm text-amber-800 dark:text-amber-200 leading-relaxed">
                    <strong>{T('dashboard.passwordNotice.title', 'Akaun anda belum ada password.')}</strong>{' '}
                    {T('dashboard.passwordNotice.desc', 'Set sekarang supaya anda boleh login di browser lain dan pada Extension SESAAT.')}
                </span>
                <span className="material-symbols-outlined text-amber-500 text-[18px] flex-shrink-0">chevron_right</span>
            </button>
        </section>
    );
}

/* ── Satu kad produk dalam seksyen Produk Baru ─────────────── */
function NewProductCard({ product, onClick, T }) {
    const thumb = product.imageUrl || null;
    const name = product.name
        || T('dashboard.newProducts.fallbackName', `Produk #${product.number ?? ''}`, { n: product.number ?? '' });

    return (
        <button
            onClick={onClick}
            className="group flex flex-col bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-2xl overflow-hidden hover:border-indigo-300 dark:hover:border-indigo-600 hover:shadow-lg hover:shadow-indigo-500/10 hover:-translate-y-1 transition-all duration-300 text-left w-full"
        >
            {/* Thumbnail */}
            <div className="relative w-full aspect-square bg-slate-100 dark:bg-slate-800 overflow-hidden">
                {thumb ? (
                    <img src={thumb} alt={name} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
                ) : (
                    <div className="w-full h-full flex items-center justify-center">
                        <span className="material-symbols-outlined text-4xl text-slate-300 dark:text-slate-600" style={{ fontVariationSettings: "'FILL' 1" }}>image</span>
                    </div>
                )}
                {/* Intelligence badge */}
                <span className="absolute top-2 right-2 text-[10px] font-bold px-2 py-0.5 rounded-full bg-emerald-100 text-emerald-700 dark:bg-emerald-900/40 dark:text-emerald-400">
                    Ready
                </span>
            </div>
            {/* Info */}
            <div className="p-3">
                <p className="text-sm font-semibold text-slate-800 dark:text-slate-100 truncate">{name}</p>
                {product.link && (
                    <p className="text-[11px] text-slate-400 truncate mt-0.5">{product.link}</p>
                )}
            </div>
        </button>
    );
}

/* ── Seksyen Produk Baru (pustaka System) ──────────────────── */
export function NewProductsSection({ products = [], t }) {
    const navigate = useNavigate();
    const T = ambilTeks(t);
    if (products.length === 0) return null;

    return (
        <section className="w-full px-4 sm:px-6 pt-8 pb-4">
            <div className="flex items-center justify-between mb-5">
                <div>
                    <p className="text-xs font-bold uppercase tracking-[0.15em] text-indigo-500 dark:text-indigo-400 mb-1">
                        {T('dashboard.newProducts.eyebrow', 'Terbaru')}
                    </p>
                    <h2 className="text-xl font-bold text-slate-800 dark:text-white">
                        {T('dashboard.newProducts.title', 'Produk Baru')}
                    </h2>
                </div>
                <button
                    onClick={() => navigate('/products/system')}
                    className="text-sm font-semibold text-indigo-500 hover:text-indigo-600 dark:hover:text-indigo-400 flex items-center gap-1 transition-colors"
                >
                    {T('dashboard.newProducts.seeAll', 'Lihat semua')}
                    <span className="material-symbols-outlined text-[16px]">arrow_forward</span>
                </button>
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-5 xl:grid-cols-10 gap-3">
                {products.map(product => (
                    <NewProductCard
                        key={product.id}
                        product={product}
                        T={T}
                        onClick={() => navigate('/products/system')}
                    />
                ))}
            </div>
        </section>
    );
}

/* ── Footer ────────────────────────────────────────────────── */
/* mt-auto — kekalkan footer di bawah sekali walaupun kandungan page pendek */
export function DashboardFooter() {
    return (
        <footer className="mt-auto border-t border-slate-200/60 dark:border-slate-800/60 py-8">
            <div className="w-full px-4 sm:px-6 flex flex-col sm:flex-row items-center justify-between gap-4">
                <div className="flex items-center gap-2">
                    <div className="w-6 h-6 bg-gradient-to-br from-blue-500 to-indigo-600 rounded-lg flex items-center justify-center">
                        <span className="material-symbols-outlined text-white text-sm" style={{ fontVariationSettings: "'FILL' 1" }}>bolt</span>
                    </div>
                    <span className="text-sm font-bold text-slate-700 dark:text-slate-300">SESAAT</span>
                    <span className="text-[9px] font-black uppercase tracking-wider px-1.5 py-0.5 rounded bg-indigo-100 dark:bg-indigo-900/30 text-indigo-500 dark:text-indigo-400">Beta</span>
                </div>
                <p className="text-xs text-slate-400 dark:text-slate-500">© {new Date().getFullYear()} SESAAT. All rights reserved.</p>
            </div>
        </footer>
    );
}
